import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import {useEffect, useRef, useState} from 'react';
import {COLORS, EXPORTING} from '../../../utils/constants';
import {groupBy} from '../../../utils/groupBy';
import {
    AUTONOMOUS_KEY,
    BARELY_AUTONOMOUS_KEY,
    latestPerUoa,
    meanOf,
    percentageOf
} from '../../../utils/wia';
import {roundTo} from '../../../utils/round';

interface IAutonomyData {
    submissions: any[];
    uoa: string | number
}

interface ISubmissionData {
    autonomous: number;
    barelyAutonomous: number;
}

const countAutonomy = (submissions: any[]): ISubmissionData => ({
    autonomous: submissions.reduce((a, s) => a + (s[AUTONOMOUS_KEY] || 0), 0),
    barelyAutonomous: submissions.reduce((a, s) => a + (s[BARELY_AUTONOMOUS_KEY] || 0), 0),
});

const totalOf = (counts: ISubmissionData): number =>
    counts.autonomous + counts.barelyAutonomous;

const toPercentages = (counts: ISubmissionData): ISubmissionData => ({
    autonomous: percentageOf(counts.autonomous, totalOf(counts)),
    barelyAutonomous: percentageOf(counts.barelyAutonomous, totalOf(counts)),
});

const Autonomy = ({submissions, uoa}: IAutonomyData) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();

    const calculateSubmissions = () => {
        const latest = latestPerUoa(submissions);

        const currentSubmissions = toPercentages(
            countAutonomy(latest.filter((submission: any) => submission.uoa_parent_id === uoa))
        );

        const projects: ISubmissionData[] = Object.values(groupBy(latest, 'uoa_parent_id'))
            .map((project: any) => countAutonomy(project))
            .filter((counts: ISubmissionData) => totalOf(counts) > 0)
            .map(toPercentages);

        const allSubmissions: ISubmissionData = {
            autonomous: meanOf(projects.map(project => project.autonomous)),
            barelyAutonomous: meanOf(projects.map(project => project.barelyAutonomous)),
        };

        const dashboardOptions: any = {
            chart: {
                height: 400,
                type: 'column',
                style: {
                    fontFamily: 'Rubik, sans-serif'
                },
                backgroundColor: '#fafafa'
            },
            title: {
                text: 'Composizione beneficiari attesi - autonomia',
                align: 'left'
            },
            xAxis: {
                categories: ['Il mio progetto', 'Media dei progetti']
            },
            yAxis: {
                min: 0,
                max: 100,
                labels: {
                    format: '{value}%'
                },
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
            },
            tooltip: {
                valueSuffix: '%'
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    stacking: 'percent',
                }
            },
            series: [
                {
                    name: '% di beneficiari anziani poco autonomi',
                    color: COLORS.red,
                    data: [
                        roundTo(currentSubmissions.barelyAutonomous),
                        roundTo(allSubmissions.barelyAutonomous),
                    ]
                },
                {
                    name: '% di beneficiari anziani autonomi',
                    color: COLORS.blue,
                    data: [
                        roundTo(currentSubmissions.autonomous),
                        roundTo(allSubmissions.autonomous),
                    ]
                }
            ],
            exporting: EXPORTING
        };

        setOptions(dashboardOptions);
    };

    useEffect(() => {
        calculateSubmissions();
    }, []);

    return (
        <Card>
            <CardContent>
                {
                    options && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartComponentRef}
                        />
                    )
                }
            </CardContent>
        </Card>
    );
};

export default Autonomy;
